// Nomad-mon Go! — SC46 BACK NAVIGATION + THE EXIT CONFIRMATION.
//
// Jack: "if you're on a module and you're on your phone and you swipe back or
// press the back button, instead of exiting the app completely... make it so
// that behavior takes them back to the main screen instead, to prevent
// unwanted exits."
//
// THE TRAP, AND THE RULE THAT AVOIDS IT.
// Always swallowing back makes the app impossible to leave, which is a worse
// bug than the one being fixed. The rule here is:
//
//     back means "up one level" when there IS a level above,
//     and "leave" when there isn't.
//
// An open overlay closes first; then a screen returns to the title; and only
// from the title, with nothing open, does back actually exit the site.
//
// HOW THE HISTORY IS KEPT CLEAN — this is the part naive versions get wrong.
// The obvious implementation pushes one entry per screen and per overlay, and
// a player who wandered five screens deep then needs five back presses to get
// out (the reported symptom is "eight presses to escape"). This keeps at most
// ONE extra entry on the stack at any time:
//
//   base entry            <- always there, the document's own entry
//   guard entry           <- present IFF depth() > 0, i.e. there is somewhere
//                            to go up TO
//
// popstate fires AFTER the browser has already dropped the guard, so we are
// standing on base. We take one step up, and if there is STILL somewhere to go
// (depth() > 0 again) we re-push the guard. history.length therefore never
// grows with navigation depth, and `back` from the title is a real exit
// because at that moment no guard exists to absorb it.
//
// MID-BATTLE IS THE ONE DESTRUCTIVE EXIT, so it is the one that is guarded.
// Jack: "if someone tries to exit in any fashion in the middle of a battle,
// show a really nice looking big on screen confirmation, beautifully
// designed." That confirmation is what LETS back be instant and silent
// everywhere else.

// NAV AUDIT (v136) — ONE MODEL FOR EVERY LAYER.
//   overlays (sheets, drawers, dialogs, viewers) close from a top-right X,
//     from a tap on their dimmed backdrop, from Escape, and from Back;
//   screens go home from the top-left medal, and Back/Escape do the same thing
//     the medal does on that screen (the `goTitle` hook routes per screen);
//   Escape is Back for a keyboard: it runs the same history step, so the two
//     can never disagree and the guard entry stays in sync;
//   focus returns to whatever opened the layer once it closes.
const levels = [];        // overlay levels, sorted by z DESC (topmost first)
let hooks = null;         // { currentScreen, goTitle, battleActive, confirmExit }
let guard = false;        // is our extra history entry currently on the stack?
let armed = false;
let unwinding = false;    // a history.back() WE issued is in flight
let lastAction = null;    // debug: what the most recent back press actually did

/**
 * Register one dismissable layer. Modules register their OWN overlays so this
 * file never has to import them — a nav module that imports every screen is a
 * circular-import factory, and it also means a new overlay is one call away
 * from being back-aware instead of an edit here.
 *
 * z orders the layers; the HIGHEST open z is what a back press closes.
 */
export function registerLevel({ name, z, isOpen, close }) {
  levels.push({ name, z, isOpen, close, wasOpen: false, returnTo: null });
  levels.sort((a, b) => b.z - a.z);
}

/** The topmost OPEN overlay, or null. */
function topOverlay() {
  for (const l of levels) {
    let open = false;
    try { open = !!l.isOpen(); } catch { open = false; }
    if (open) return l;
  }
  return null;
}

/**
 * How many levels are above "the title with nothing open".
 * 0 means back should genuinely leave the site.
 */
export function depth() {
  if (topOverlay()) return 2;
  const s = hooks && hooks.currentScreen ? hooks.currentScreen() : 'title';
  return s && s !== 'title' ? 1 : 0;
}

function pushGuard() {
  if (guard) return;
  try { history.pushState({ nmGuard: 1 }, ''); guard = true; } catch { /* no-op */ }
}

function dropGuard() {
  // Only ever called when the browser has ALREADY consumed the guard entry
  // (i.e. from inside popstate). Never calls history.back() itself — doing so
  // from a popstate handler is how these implementations end up in a loop.
  guard = false;
}

/**
 * Keep the guard in sync with the current depth. Called after every screen
 * change and every overlay open/close, so the stack is correct at the moment
 * the player presses back rather than being reconstructed then.
 */
export function sync() {
  trackFocus();
  if (!armed || unwinding) return;
  if (depth() > 0) { pushGuard(); return; }
  // BACK AT DEPTH 0 MUST LEAVE ON THE FIRST PRESS.
  // A guard left over from in-app navigation (the HOME button, the
  // interstitial's CONTINUE) would swallow exactly one press and do nothing
  // visible — the "eight presses to escape" bug in miniature, and the precise
  // symptom this design exists to avoid. So unwind it eagerly. `unwinding`
  // makes the resulting popstate a no-op instead of a phantom step-up, and
  // stops the sync timer from firing a second back() into the same gap.
  if (guard) {
    unwinding = true;
    guard = false;
    try { history.back(); } catch { unwinding = false; }
  }
}

// ---- focus return. Overlays open from many modules; rather than teach each
// one, remember the last control the player touched, bind it to a level when
// that level is first seen open, and hand focus back when it closes (only if
// focus was left nowhere useful — on <body> or inside the now-hidden layer).
let lastTrigger = null;
function noteTrigger(e) {
  const t = e.target && e.target.closest && e.target.closest('button, a[href], input, select, textarea, [tabindex], [role="button"]');
  if (t) lastTrigger = t;
}
function shown(el) {
  if (!el || !el.isConnected) return false;
  const r = el.getBoundingClientRect();
  if (r.width < 1 || r.height < 1) return false;
  const cs = getComputedStyle(el);
  return cs.visibility !== 'hidden' && cs.display !== 'none';
}
function trackFocus() {
  for (const l of levels) {
    let open = false;
    try { open = !!l.isOpen(); } catch { open = false; }
    if (open && !l.wasOpen) l.returnTo = lastTrigger;
    if (!open && l.wasOpen) {
      const back = l.returnTo; l.returnTo = null;
      const a = document.activeElement;
      if (back && (!a || a === document.body || !shown(a)) && shown(back)) {
        try { back.focus({ preventScroll: true }); } catch { /* gone */ }
      }
    }
    l.wasOpen = open;
  }
}

/**
 * Escape = Back. Goes through the same history step when the guard is armed
 * (so the popstate path does the work and the stack stays consistent), or
 * steps up directly when it is not. Returns false at depth 0: nothing to do.
 */
export function escapeKey() {
  if (!hooks || depth() === 0) return false;
  if (guard && !unwinding) { try { history.back(); return true; } catch { /* fall through */ } }
  lastAction = stepUp();
  sync();
  return true;
}

/** One step up. Returns what it did, for the debug surface and the suite. */
function stepUp() {
  const ov = topOverlay();
  if (ov) { try { ov.close(); } catch { /* already gone */ } return `close:${ov.name}`; }
  const s = hooks.currentScreen();
  if (s && s !== 'title') {
    // THE ONE GUARDED EXIT. A battle in progress is the only place where
    // stepping up destroys something — in multiplayer it abandons a real
    // person mid-game — so it asks first. Everything else is silent.
    if (hooks.battleActive()) { hooks.confirmExit(); return 'confirm'; }
    hooks.goTitle();
    return 'title';
  }
  return 'exit';
}

function onPop() {
  // Our own unwind landing. It is not a user gesture and must not move the app.
  if (unwinding) { unwinding = false; lastAction = 'unwind'; return; }
  // The entry we were standing on is gone; whatever it was, we are not on the
  // guard any more.
  dropGuard();
  const action = stepUp();
  lastAction = action;
  // 'exit' means nothing was above the title: do NOT re-push, because the next
  // press must reach the browser. Otherwise re-arm the absorber.
  if (action !== 'exit' && depth() > 0) pushGuard();
}

/**
 * hooks = {
 *   currentScreen(): string,
 *   goTitle(): void,
 *   battleActive(): boolean,
 *   confirmExit(): void,       // shows the big on-screen confirmation
 *   onUnloadRisk(): void,      // fires on pagehide during a battle (MP notify)
 * }
 */
export function initNav(h) {
  hooks = h;
  armed = true;
  try { history.replaceState({ nmBase: 1 }, ''); } catch { /* no-op */ }
  window.addEventListener('popstate', onPop);
  document.addEventListener('pointerdown', noteTrigger, true);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') noteTrigger(e);
    // capture phase, so ONE press closes exactly ONE layer even where a module
    // has its own Escape handler for the same layer
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    if (escapeKey()) { e.preventDefault(); e.stopImmediatePropagation(); }
  }, true);

  // TAB CLOSE / RELOAD / TYPED NAVIGATION. A custom dialog is impossible here
  // — every browser replaced page-authored beforeunload text with its own
  // generic prompt years ago, and only a native prompt can actually block the
  // navigation. So: ask the browser to prompt, and independently make sure the
  // opponent is told, because THAT is the part that matters in multiplayer.
  window.addEventListener('beforeunload', (ev) => {
    if (!hooks.battleActive()) return;
    ev.preventDefault();
    ev.returnValue = '';
    return '';
  });
  // pagehide is the only event that reliably fires on mobile when the tab is
  // closed or the app is swiped away; beforeunload is not dispatched on iOS.
  // sendBeacon survives the teardown, so the opponent learns immediately
  // instead of waiting out the server's 60s grace.
  window.addEventListener('pagehide', () => {
    if (hooks.battleActive() && hooks.onUnloadRisk) {
      try { hooks.onUnloadRisk(); } catch { /* nothing left to do */ }
    }
  });

  sync();
}

/** Debug surface for the smoke suite. */
export function navDebug() {
  return {
    armed, guard, depth: depth(), lastAction,
    top: (topOverlay() || {}).name || null,
    screen: hooks && hooks.currentScreen ? hooks.currentScreen() : null,
    historyLen: history.length,
    levels: levels.map(l => l.name),
  };
}

/** The suite drives back through the real mechanism, not through a shim. */
export function debugBack() { history.back(); }
